import React, { useState } from "react";

// The contact form posts to our own /api/contact endpoint (same origin, proxied
// to the Node backend), which delivers the message to info@corelinkai.com via
// Google Workspace SMTP. No third-party relay, no credentials in the frontend.
const emptyForm = { name: "", email: "", phone: "", company_website: "", message: "" };

export default function Contact({ data }) {
  const [form, setForm] = useState(emptyForm);
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const websiteRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    if (!websiteRegex.test(form.company_website)) {
      alert("Please enter a valid website (e.g. mywebsite.com or https://mywebsite.com).");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSuccess("Message sent — we'll be in touch within two business days.");
      setTimeout(() => {
        e.target.reset();
        setForm(emptyForm);
        setSuccess("");
        setSubmitting(false);
      }, 4000);
    } catch (err) {
      console.error(err);
      alert("An error occurred while sending the message. Please try again.");
      setSubmitting(false);
    }
  };

  if (!data) return null;

  return (
    <section id="contact" className="section">
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{data.eyebrow}</span>
          <h2 className="display-2">{data.title}</h2>
          <p className="lede" style={{ marginTop: "1.5rem" }}>{data.lede}</p>
        </div>

        <div className="contact-grid">
          <form className="contact-form" onSubmit={onSubmit} noValidate>
            <div className="field-row">
              <div className="field">
                <label htmlFor="name">Name</label>
                <input id="name" name="name" type="text" placeholder="Your full name"
                       value={form.name} onChange={onChange} required />
              </div>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input id="email" name="email" type="email" placeholder="you@company.com"
                       value={form.email} onChange={onChange} required />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label htmlFor="phone">Phone</label>
                <input id="phone" name="phone" type="text" placeholder="+44 …"
                       value={form.phone} onChange={onChange} required />
              </div>
              <div className="field">
                <label htmlFor="company_website">Company website</label>
                <input id="company_website" name="company_website" type="text"
                       placeholder="company.com"
                       value={form.company_website} onChange={onChange} required />
              </div>
            </div>

            <div className="field">
              <label htmlFor="message">Project outline</label>
              <textarea id="message" name="message" rows="5"
                        placeholder="What are you trying to build? Include constraints, timeline, and context."
                        value={form.message} onChange={onChange} required />
            </div>

            {success && <div className="form-success" role="status">{success}</div>}

            <button className="btn btn--solid form-submit" type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send message"}
              {!submitting && <span className="btn-arrow" aria-hidden="true">→</span>}
            </button>
          </form>

          <aside className="contact-info-block">
            <div className="contact-info-item">
              <span className="mono-label">Email</span>
              <a href={`mailto:${data.email}`}>{data.email}</a>
            </div>

            {data.phone && (
              <div className="contact-info-item">
                <span className="mono-label">Phone</span>
                <p>{data.phone}</p>
              </div>
            )}

            <div className="contact-info-item">
              <span className="mono-label">Office</span>
              <p>{data.address}</p>
            </div>

            <div className="contact-social">
              {data.social?.linkedin && (
                <a href={data.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
              {data.social?.twitter && (
                <a href={data.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter / X">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
                  </svg>
                </a>
              )}
              <a href="/blog/" aria-label="Blog" title="Blog">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M4 3.5h16a.5.5 0 0 1 .5.5v16a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5zM7 7.5v2h10v-2H7zm0 4v2h10v-2H7zm0 4v2h7v-2H7z"/>
                </svg>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
